import React, { createContext, useCallback, useContext, useEffect, useSyncExternalStore } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { type CacheRequest, ServerCache, serverCache } from './serverCache';

const CacheContext = createContext(serverCache);
export const ServerCacheProvider: React.FC<{ children: React.ReactNode; cache?: ServerCache }> = ({ children, cache = serverCache }) => (
  <CacheContext.Provider value={cache}>{children}</CacheContext.Provider>
);

export function useServerQuery<T>(request: CacheRequest, enabled = true) {
  const cache = useContext(CacheContext);
  const { isLoading } = useAuth();
  useSyncExternalStore(cache.subscribe, cache.getRevision, cache.getRevision);
  const ready = enabled && !isLoading && cache.getScope() !== null;
  const snapshot = ready ? cache.read<T>(request) : undefined;
  const { resource, key, url } = request;
  const scope = cache.getScope();
  const invalidationVersion = snapshot?.invalidationVersion || 0;
  const needsData = ready && snapshot?.data === undefined && !snapshot?.error && !snapshot?.validating;
  useEffect(() => {
    if (ready) void cache.fetch<T>({ resource, key, url }).catch(() => { /* Render the query error below. */ });
  }, [cache, resource, key, url, ready, scope, invalidationVersion, needsData]);
  useEffect(() => {
    if (!ready) return;
    const refreshVisible = () => {
      if (!document.hidden) void cache.fetch<T>({ resource, key, url }).catch(() => {});
    };
    window.addEventListener('focus', refreshVisible);
    document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      window.removeEventListener('focus', refreshVisible);
      document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [cache, resource, key, url, ready, scope]);
  const refetch = useCallback(() => cache.fetch<T>({ resource, key, url }, true).catch(() => undefined), [cache, resource, key, url]);
  return {
    data: snapshot?.data,
    error: snapshot?.error,
    loading: enabled && (!ready || (snapshot?.data === undefined && !snapshot?.error)),
    refreshing: Boolean(snapshot?.validating && snapshot?.data !== undefined),
    refetch,
  };
}
